import React, { useState, useEffect } from 'react';
import { Table, Card, Input, Button, message, Tooltip, Tag, Space, Alert } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import axiosInstance from '../../../configs';

const { Search } = Input;

export default function SettingsTable() {
    const [settings, setSettings] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingKey, setEditingKey] = useState('');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/referensi-finance/settings');
            // HANYA tampilkan invoice format - tarif harus dari Harga Dasar
            const invoiceFormatOnly = (res.data.data || []).filter(s =>
                s.setting_key && s.setting_key.includes('INVOICE_NUMBER_FORMAT')
            );
            setSettings(invoiceFormatOnly);
            setFilteredData(invoiceFormatOnly);
        } catch (err) {
            message.error('Gagal mengambil data settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        const filtered = settings.filter(item =>
            item.setting_label?.toLowerCase().includes(value.toLowerCase()) ||
            item.setting_key?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredData(filtered);
    };

    const handleEdit = (key) => {
        setEditingKey(key);
    };

    const handleCancel = () => {
        setEditingKey('');
        fetchSettings();
    };

    const handleSave = async (record) => {
        try {
            await axiosInstance.put(`/referensi-finance/settings/${record.id_setting}`, {
                setting_value: record.setting_value,
                setting_label: record.setting_label,
                setting_description: record.setting_description
            });
            message.success('Setting berhasil disimpan');
            setEditingKey('');
            fetchSettings();
        } catch (err) {
            message.error('Gagal menyimpan setting');
        }
    };

    const handleChange = (value, key, field) => {
        const newData = [...settings];
        const index = newData.findIndex(item => item.id_setting === key);
        if (index > -1) {
            newData[index][field] = value;
            setSettings(newData);
        }
    };

    const isEditing = (record) => record.id_setting === editingKey;

    const columns = [
        {
            title: 'Setting Key',
            dataIndex: 'setting_key',
            key: 'setting_key',
            width: 300,
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Label',
            dataIndex: 'setting_label',
            key: 'setting_label',
            width: 250,
        },
        {
            title: 'Value / Format',
            dataIndex: 'setting_value',
            key: 'setting_value',
            width: 250,
            render: (text, record) => {
                const editing = isEditing(record);
                return editing ? (
                    <Input
                        value={record.setting_value}
                        onChange={e => handleChange(e.target.value, record.id_setting, 'setting_value')}
                        placeholder="Enter value or pattern"
                    />
                ) : (
                    <strong>{text}</strong>
                );
            }
        },
        {
            title: 'Deskripsi / Pattern',
            dataIndex: 'setting_description',
            key: 'setting_description',
            render: (text, record) => {
                if (record.setting_key?.includes('INVOICE_NUMBER_FORMAT')) {
                    return (
                        <Tooltip title="Available patterns: {NOPEN}, {YEAR}, {MONTH}, {TYPE}, {SEQ}">
                            <div style={{ fontSize: '12px', color: '#666' }}>
                                {text || 'Pattern: {NOPEN}, {YEAR}, {MONTH}, {TYPE}, {SEQ}'}
                            </div>
                        </Tooltip>
                    );
                }
                return <div style={{ fontSize: '12px', color: '#666' }}>{text}</div>;
            }
        },
        {
            title: 'Aksi',
            key: 'action',
            width: 120,
            align: 'center',
            render: (_, record) => {
                const editing = isEditing(record);
                return editing ? (
                    <Space>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            size="small"
                            onClick={() => handleSave(record)}
                        >
                            Save
                        </Button>
                        <Button
                            icon={<CloseOutlined />}
                            size="small"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </Space>
                ) : (
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEdit(record.id_setting)}
                    >
                        Edit
                    </Button>
                );
            }
        }
    ];

    return (
        <Card>
            <Alert
                message="Catatan: Tarif Handling, Cukai, dan Storage diatur di tab 'Harga Dasar', bukan di sini."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <div style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Cari setting..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    style={{ width: 300 }}
                    onSearch={handleSearch}
                    onChange={(e) => handleSearch(e.target.value)}
                />
            </div>
            <Table
                dataSource={filteredData}
                columns={columns}
                rowKey="id_setting"
                loading={loading}
                pagination={{ pageSize: 10, showTotal: (total) => `Total ${total} items` }}
            />
        </Card>
    );
}
